import api from './api';
import type { ChatMessage, Lesson, LessonVersion, PaginatedResponse } from '../types';

export interface LessonQueryParams {
  page?: number;
  per_page?: number;
  status?: string;
  cefr_level?: string;
  search?: string;
  author_id?: string;
}

export interface LessonCreateData {
  title: string;
  cefr_level: string;
  topic: string;
  duration_min: number;
}

export interface LessonUpdateData {
  title?: string;
  cefr_level?: string;
  topic?: string;
  duration_min?: number;
  status?: string;
}

export interface LessonVersionSaveData {
  lesson_plan?: object;
  slide_mapping?: object;
  slides_html?: object;
  final_html?: string;
  base_prompt?: string;
  custom_instructions?: string;
  intake_chat_history?: ChatMessage[];
  phase?: number;
}

export const lessonService = {
  getAll: async (params?: LessonQueryParams): Promise<PaginatedResponse<Lesson>> => {
    const response = await api.get<{ lessons: Lesson[]; total: number; page: number; per_page: number }>('/lessons', { params });
    return {
      data: response.data.lessons ?? [],
      meta: {
        page: response.data.page,
        per_page: response.data.per_page,
        total: response.data.total,
      },
    };
  },

  getById: async (id: string): Promise<{ lesson: Lesson; version: LessonVersion }> => {
    const response = await api.get<{ lesson: Lesson; version: LessonVersion }>(`/lessons/${id}`);
    return response.data;
  },

  create: async (data: LessonCreateData): Promise<Lesson> => {
    const response = await api.post<{ lesson: Lesson; version: LessonVersion }>('/lessons', data);
    return response.data.lesson;
  },

  update: async (id: string, data: LessonUpdateData): Promise<Lesson> => {
    const response = await api.patch<Lesson>(`/lessons/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/lessons/${id}`);
  },

  getVersion: async (id: string, version: number): Promise<LessonVersion> => {
    const response = await api.get<LessonVersion>(`/lessons/${id}/versions/${version}`);
    return response.data;
  },

  saveVersion: async (
    id: string,
    version: number,
    data: LessonVersionSaveData
  ): Promise<LessonVersion> => {
    const response = await api.patch<LessonVersion>(
      `/lessons/${id}/versions/${version}`,
      data
    );
    return response.data;
  },

  submit: async (id: string, versionNumber: number): Promise<void> => {
    await api.post(`/lessons/${id}/versions/${versionNumber}/submit`);
  },

  duplicate: async (id: string): Promise<Lesson> => {
    const response = await api.post<{ lesson: Lesson; version: LessonVersion }>(`/lessons/${id}/duplicate`);
    return response.data.lesson;
  },

  acquireLock: async (id: string): Promise<void> => {
    await api.post(`/lessons/${id}/lock`);
  },

  releaseLock: async (id: string): Promise<void> => {
    await api.delete(`/lessons/${id}/lock`);
  },

  publish: async (id: string): Promise<void> => {
    await api.post(`/lessons/${id}/publish`);
  },

  createVersion: async (id: string): Promise<LessonVersion> => {
    const response = await api.post<LessonVersion>(`/lessons/${id}/versions`);
    return response.data;
  },
};
